<template>
	<v-form
			v-model="formIsValid.addPlace"
			ref="addContactForm"
			:class="{ 'mb-8': showContactForm }"
	>

		<ui-box
				:additional-validations="validations"
				new-forms
		>
			<form-title title="Add Places">
				Great! Now let's add places people will go while using your pass.

				<template v-slot:button>
					<slot name="button"></slot>
				</template>
			</form-title>

			<form-divider inner/>

			<!-- Search Place Field: Start -->
			<v-row v-if="!showManualPlace" class="position-relative z-2">
				<v-col id="search-place-input">
					<v-autocomplete
							v-if="!selectedPlace"
							v-model="selectedPlace"
							:autofocus="selectedPlaceCleared"
							class="rounded-l-0 rounded-r-lg"
							clearable
							:filter="() => true"
							hide-details="auto"
							:items="suggestions"
							item-text="description"
							item-value="place_id"
							label="Place Name"
							outlined
							return-object
							:search-input.sync="search"
							@focus="selectedPlaceCleared = false"
							@input="getPlaceDetails"
					>
						<template v-slot:prepend>
							<div class="rounded-l-lg rounded-r-0 white">
								<v-btn
										class="pe-3 ps-5 rounded-l-lg rounded-r-0"
										depressed
										outlined
										style="border-color: rgba(0, 0, 0, 0.38); margin-right: -1px;"
										x-large
										@click="startManualPlace"
								>
									<v-icon size="24">mdi-checkbox-blank-outline</v-icon>
									Manual
								</v-btn>
							</div>
						</template>
						<template v-slot:item="data">
							<template v-if="data.item.isExisting">
								<v-list-item class="grey lighten-5">
									<v-list-item-content>
										<v-list-item-title
												class="grey--text text--darken-1"
												v-html="wrapAutocompleteSearchTextResult(data.item.description, search)">
										</v-list-item-title>
									</v-list-item-content>
									<v-list-item-icon>
										<v-chip small>{{
												pass.id ? 'Already In Pass' : 'Already In Network'
											}}
										</v-chip>
									</v-list-item-icon>
								</v-list-item>
							</template>
							<template v-else>
								<v-list-item v-bind="data.attrs" v-on="data.on">
									<v-list-item-content>
										<v-list-item-title
												v-html="wrapAutocompleteSearchTextResult(data.item.description, search)">
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</template>
						</template>
					</v-autocomplete>
					<template v-else>
						<v-text-field
								class="rounded-lg"
								clearable
								disabled
								hide-details="auto"
								label="Place Name"
								outlined
								:value="selectedPlace.name"
								@click="handleRefocusSearch"
						></v-text-field>
					</template>
				</v-col>
			</v-row>
			<!-- Search Place Field: End -->

			<!-- Manual Place Field: Start -->
			<v-row v-if="showManualPlace" class="position-relative z-2">
				<v-col id="manual-place-input">
					<v-text-field
							v-model="manualPlace.name"
							class="rounded-l-0 rounded-r-lg"
							hide-details="auto"
							label="Point of Interest Name"
							outlined
					>
						<template v-slot:prepend>
							<div class="rounded-l-lg rounded-r-0 white">
								<v-btn
										class="pe-3 ps-5 rounded-l-lg rounded-r-0"
										depressed
										outlined
										style="border-color: rgba(0, 0, 0, 0.38); margin-right: -1px;"
										x-large
										@click="endManualPlace"
								>
									<v-icon size="24">mdi-checkbox-marked</v-icon>
									Manual
								</v-btn>
							</div>
						</template>
					</v-text-field>
				</v-col>
			</v-row>
			<!-- Manual Place Field: End -->

			<div
					v-if="!selectedPlace"
					class="grey lighten-5 mt-n8 position-relative pa-1 pt-5 px-3 rounded-lg z-1"
					:class="{ 'mb-8': showManualPlace || showContactForm }"
					style="border: solid 1px rgba(0, 0, 0, 0.08) !important;"
			>
				<span class="text-caption text-no-wrap">Manual Entry</span>
				<span
						v-if="$vuetify.breakpoint.smAndUp"
						class="grey--text text-caption"
						style="padding: 2px 0 0 2px;"
				>
					- Can't find your place on the Google search?
				</span>
			</div>

			<!-- Search Place Map: Start -->
			<v-row v-if="selectedPlace">
				<v-col>
					<PlaceMap
							v-model="selectedPlace.geometry.location"
							draggable
							@dragend="updateLocation"
					/>
				</v-col>
			</v-row>
			<!-- Search Place Map: End -->

			<!-- Manual Place Map: Start -->
			<v-row v-if="showManualPlace && manualPlace.name">
				<v-col>
					<PlaceMap
							v-model="manualPlace.geometry.location"
							draggable
							@initLocation="setInitialLocation"
							@dragend="updateManualLocation"
					/>
				</v-col>
			</v-row>
			<!-- Manual Place Map: End -->

			<form-divider v-if="showContactForm" inner mt="8"/>

			<template v-if="showContactForm">

				<div>
					<form-subsection title="Contact Information">
						If you have contact information for this place, add it now. Otherwise, you can
						add this information later.

						<template v-slot:button>
							<slot name="button"></slot>
						</template>
					</form-subsection>

					<v-row>
						<v-col cols="12" md="4" lg="4">
							<v-text-field
									v-model="newContact.first_name"
									class="rounded-lg"
									clearable
									hide-details="auto"
									label="First Name"
									outlined
							></v-text-field>
						</v-col>
						<v-col cols="12" md="4" lg="4">
							<v-text-field
									v-model="newContact.last_name"
									class="rounded-lg"
									clearable
									hide-details="auto"
									label="Last Name"
									outlined
							></v-text-field>
						</v-col>
						<v-col cols="12" md="4" lg="4">
							<v-text-field
									v-model="newContact.email"
									class="rounded-lg"
									clearable
									hide-details="auto"
									label="Email"
									outlined
									:rules="rules.email"
							></v-text-field>
						</v-col>
						<v-col cols="12" md="6" lg="4">
							<vue-tel-input-vuetify
									ref="phone"
									v-model="newContact.phone"
									append-icon="mdi-menu-down"
									class="flex-grow-1 rounded-lg"
									clearable
									:defaultCountry="'us'"
									hide-details="auto"
									label="Primary Phone"
									mode="international"
									outlined
									:preferred-countries="['us', 'ca']"
									:rules="rules.phone"
									@input="phoneInputEvent"
									@keydown="phoneKeyEvent"
							></vue-tel-input-vuetify>
						</v-col>
						<v-col cols="12" md="6" lg="4">
							<vue-tel-input-vuetify
									ref="secondary_phone"
									v-model="newContact.secondary_phone"
									append-icon="mdi-menu-down"
									class="flex-grow-1 rounded-lg"
									clearable
									:defaultCountry="'us'"
									hide-details="auto"
									label="Secondary Phone"
									mode="international"
									outlined
									:preferred-countries="['us', 'ca']"
									:rules="rules.phone"
									@input="phoneInputEvent"
									@keydown="phoneKeyEvent"
							></vue-tel-input-vuetify>
						</v-col>
					</v-row>

					<form-divider inner mt="8"/>

				</div>

			</template>

			<v-row v-if="showContactForm || showAddPlace">
				<v-col class="ml-auto" cols="auto">
					<div class="d-flex" v-if="!showManualPlace">
						<v-btn
								v-if="selectedPlace"
								class="me-2 rounded-lg"
								color="grey darken-1"
								depressed
								text
								@click="selectedPlace = null; selectedPlaceCleared = true;"
						>
							Cancel
						</v-btn>
						<v-btn
								class="rounded-lg"
								color="secondary"
								depressed
								:disabled="!selectedPlace"
								:loading="updatingAddress"
								@click="addPlace"
						>
							Add
						</v-btn>
					</div>
					<template v-if="showManualPlace">
						<v-btn
								class="rounded-lg"
								color="secondary"
								depressed
								:disabled="!manualPlace.name"
								:loading="updatingAddress"
								@click="addManualPlace"
						>
							Add
						</v-btn>
					</template>
				</v-col>
			</v-row>

		</ui-box>
	</v-form>
</template>

<script>
import { BwPlace, Place, wrapAutocompleteSearchTextResult } from '../../helpers';

export default {
	name: "AddPlace",
	props: {
		error: String,
		extraData: {
			type: Object,
			default: () => ({})
		},
		partner: {
			type: Object,
			default: () => ({})
		},
		pass: {
			type: Object,
			default: () => ({})
		},
		places: {
			type: Array,
			default: () => []
		},
		user: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		const { latitude, longitude } = this.partner;
		const initialLocation = latitude && longitude
				? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
				: { lat: null, lng: null };

		return {
			contact: {},
			formIsValid: {
				addPlace: null
			},
			initialLocation,
			manualPlace: null,
			phoneIsValid: false,
			rules: {
				email: [
					v => !!v || 'Email is required.',
					v => v !== this.user.email || 'You cannot enter your own email as the contact.',
					v => {
						const pattern =
								// eslint-disable-next-line max-len
								/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
						return pattern.test(v) || 'Please enter a valid e-mail.';
					}
				],
				first_name: [v => !!v || 'First name is required.'],
				last_name: [v => !!v || 'Last name is required.'],
				phone: [
					v => !v || v && this.phoneIsValid || 'Please enter a valid phone or leave blank.'
				]
			},
			search: '',
			selectedPlace: null,
			selectedPlaceCleared: false,
			showManualPlace: false,
			suggestions: [],
			updatingAddress: false
		};
	},
	created() {
		this.newContact = new Contact();
	},
	computed: {
		availablePartnerPlaces() {
			return this.partner.places?.filter(place => !this.places.some(p => p.id === place.id)) ?? [];
		},
		validations() {
			// We are not currently adding a place, no validation mark/icon should show
			if(!this.selectedPlace || this.manualPlace?.name) return [];

			// We have started adding a place, show the validation mark/icon
			return [this.formIsValid.addPlace];
		},
		showContactForm() {
			// return (this.showManualPlace && this.manualPlace.name) || (!this.showManualPlace && this.selectedPlace);

			// TODO: Just hide this for now, just in case they want it back we can add it back quickly
			return false;
		},

		// TODO: Remove this when the above is sorted for sure.
		showAddPlace() {
			return (this.showManualPlace && this.manualPlace.name) || (!this.showManualPlace && this.selectedPlace);
		}
	},
	methods: {
		wrapAutocompleteSearchTextResult,
		async addManualPlace() {
			this.$emit('add-place', { place: this.manualPlace, manual: true });
			this.endManualPlace();
			this.search = null;
		},

		async addPlace() {
			this.$emit('add-place', { place: this.selectedPlace, manual: false });
			this.selectedPlace = null;
			this.search = null;
		},

		endManualPlace() {
			this.showManualPlace = false;
			this.manualPlace = new Place({
				name: '',
				geometry: {
					location: this.initialLocation
				}
			});
			this.updatingAddress = false;
		},

		getPlaceDetails(place) {
			if(!place) {
				this.suggestions = [];
				return;
			}

			this.selectedPlace = new Place();

			const service = new window.google.maps.places.PlacesService(document.createElement('div'));
			service.getDetails({ placeId: place.place_id }, (result, status) => {
				if(status === window.google.maps.places.PlacesServiceStatus.OK) {

					this.selectedPlace = {
						...result,
						// Don't get description here
						// description: place.description,
						geometry: {
							location: {
								lat: result.geometry.location.lat(),
								lng: result.geometry.location.lng(),
							}
						},
						formatted_address: result.formatted_address,
						formatted_phone_number: result.formatted_phone_number
					};
				}
				else {
					console.error('Error fetching place details:', status);
				}
			});
		},

		getPlacePredictions(val, types = ['establishment']) {

			return new Promise((resolve, reject) => {
				const service = new window.google.maps.places.AutocompleteService();

				const settings = {
					input: val,
					types: types,
					strictBounds: false
				};

				// 28.372436500000  /  -81.514155900000

				// Partner lat/lng if we have it - or user if we don't
				const { lat, lng } = this.initialLocation;

				// ONLY set bounds if the partner has a lat/lng
				if(!isNaN(lat) && !isNaN(lng)) {

					/*
					 * Let's restrict search priority to a 10x10 mile rectangle but then allow 
					 * searching outside the bounds by setting strictBounds: false
					 */

					// Latitude Adjustment
					const latAdjustment = 10 / 69; // 1º of lat/lng is approximately 69 miles

					// Longitude Adjustment (varies by latitude)
					const lngAdjustment = latAdjustment / Math.cos(lat * Math.PI / 180);

					const swCorner = new window.google.maps.LatLng(lat - latAdjustment, lng - lngAdjustment);
					const neCorner = new window.google.maps.LatLng(lat + latAdjustment, lng + lngAdjustment);

					settings.bounds = new window.google.maps.LatLngBounds(swCorner, neCorner);
				}

				// We use a blank country for testing and demos in all countries.
				if(this.partner.country) {
					settings.componentRestrictions = { country: this.partner.country };
				}

				// TODO: Update the restrictions to match the partner's country
				service.getPlacePredictions(settings, (predictions, status) => {
					if(status === window.google.maps.places.PlacesServiceStatus.OK) {
						resolve(predictions);
					}
					else {
						reject(status);
					}
				});
			});
		},

		handleRefocusSearch() {
			let selectedPlaceName = this.selectedPlace.name;
			this.selectedPlace = null;
			this.$nextTick(() => {
				this.search = selectedPlaceName;
				this.selectedPlaceCleared = true;
			});
		},

		phoneInputEvent(formattedNumber, { valid }) {
			// Only flip the form validity to false if NOT valid, do nothing on valid = true
			// if(!valid && formattedNumber) this.formIsValid.editPlace = false;

			// For field validation rule
			this.phoneIsValid = valid;
		},

		phoneKeyEvent(e) {
			const validCharsRegex = new RegExp(/^[0-9 +\-()]*$/);

			// Allow Control+V or Command+V for pasting
			const isPasteOperation = (e.key === 'v' || e.key === 'V') && (e.ctrlKey || e.metaKey);

			if(!validCharsRegex.test(e.key) && ![
				'ArrowLeft',
				'ArrowRight',
				'Backspace',
				'Delete',
				'Tab'
			].includes(e.key) && !isPasteOperation) {
				e.preventDefault();
			}
		},

		async searchPlaces(val) {
			if(!val) {
				this.suggestions = [];
				return;
			}

			try {
				const predictions = await this.getPlacePredictions(val);
				// this.suggestions = predictions.filter(
				// 		(prediction) =>
				// 				!this.places.some((place) => place.place_id === prediction.place_id)
				// );
				this.suggestions = predictions.map(prediction => {
					return {
						...prediction,
						isExisting: this.places.some(place => place.place_id === prediction.place_id)
					};
				});
			}
			catch (error) {
				console.error("Error fetching predictions:", error);
				// Handle error here, e.g., show an error message to the user
			}
		},

		setInitialLocation(center) {
			this.initialLocation = this.initialLocation || center;
		},

		startManualPlace() {
			this.showManualPlace = true;
			this.selectedPlace = null;
			this.search = null;
			this.manualPlace = new Place({
				name: '',
				geometry: {
					location: this.initialLocation
				}
			});
		},

		updateAddressFromLocation(location, callback) {
			this.updatingAddress = true;
			const geocoder = new window.google.maps.Geocoder();
			geocoder.geocode({ location }, (results, status) => {
				if(status === window.google.maps.GeocoderStatus.OK) {
					const interpolatedResult = results.find(result => result.geometry.location_type === 'RANGE_INTERPOLATED');
					const geometricResult = results.find(result => result.geometry.location_type === 'GEOMETRIC_CENTER');
					const approximateResult = results.find(result => result.geometry.location_type === 'APPROXIMATE');
					let result = {};
					if(interpolatedResult) {
						result = interpolatedResult;
					}
					else if(geometricResult) {
						result = geometricResult;
					}
					else if(approximateResult) {
						result = approximateResult;
					}
					else {
						console.error('No suitable result found');
					}
					this.updatingAddress = false;
					callback(result);
				}
				else {
					console.error('Error getting formatted_address:', status);
				}
			});
		},

		updateLocation(center = {}) {
			if(center?.lat && center?.lng) {
				this.selectedPlace.geometry.location = {
					lat: center.lat,
					lng: center.lng
				};
			}
			else {
				console.error('Invalid center:', center);
			}
		},

		updateManualLocation(event) {
			if(!event) return;

			this.manualPlace.geometry = event.geometry || {
				location: event
			};
			if(!this.initialLocation) {
				this.initialLocation = { ...this.manualPlace.geometry.location };
			}

			this.updateAddressFromLocation(this.manualPlace.geometry.location, ({
				formatted_address_components,
				formatted_address,
				place_id
			}) => {
				this.manualPlace = {
					...this.manualPlace,
					formatted_address_components,
					formatted_address,
					place_id
				};
			});
		},

		updateSecondaryMenu() {
			// Only do this if we're building a pass
			if(this.pass) {
				window.EventBus.$emit('get-updated-menu', {
					portal: 'partner-portal',
					location: 'passes',
					partnerId: this.partner.id,
					dealId: this.pass.id,
					method: 'places'
				});
			}
		}
	},
	watch: {
		search: debounce(function (val) {
			this.searchPlaces(val);
		}, 300)
	}
}

function Contact(contact) {
	this.first_name = null;
	this.last_name = null;
	this.email = null;
	this.phone = null;
	this.secondary_phone = null;
};
</script>

<style lang="scss">
.v-input {
	& .v-input__prepend-outer {
		margin: 0 !important;

		& button {
			font-size: 0.75rem !important;
		}
	}
}
</style>
